import React, { useState } from 'react';
import SearchBar from 'components/Autosuggest/Autosuggest';
import styles from './index.module.scss';
import { searchUserByName } from 'services/api/common';
import { deleteAddress, fetchUserAddresses } from 'services';
import { showError, stringifyAndParseObj } from 'utils';
import { Loading } from 'components/loading/Loading';
import ActionButton from 'components/ActionButton';
import scriptLoader from 'react-async-script-loader';
import _ from 'lodash';
import SingleAddressTile from 'components/SingleAddressTile';
import AccountAutosuggest from 'components/AccountAutosuggest';
import AddAddressModal from 'components/UserAddressModal/AddAddressModal';

const CustomerChannel = props => {
  const [userType, setUserType] = useState<string>('');
  const [userAddresses, setUserAddresses] = useState<Record<string, any>[]>([]);
  const [addressLoading, setAddressLoading] = useState<boolean>(false);
  const [showAddAddressButton, setShowAddAddressButton] = useState<boolean>(false);
  const [addressDeleteID, setAddressDeleteID] = useState<string>('');
  const [addressToEdit, setAddressToEdit] = useState();
  const {
    channel,
    setChannel,
    setSelectedChannelName,
    addressModalOpen,
    toggleAddressModal,
    setAddress,
    setUserId,
  } = props;

  const deleteAddressFn = async (id: string) => {
    setAddressDeleteID(id);
    try {
      await deleteAddress(id);
      fetchUserAddressesFn(channel.objectId);
    } catch (e) {
      showError(e);
    } finally {
      setAddressDeleteID('');
    }
  };
  
  const fetchUserAddressesFn = async (id: string) => {
    try {
      const response = await fetchUserAddresses(id);
      setUserAddresses(response);
    } catch (e) {
      showError(e);
    } finally {
      setAddressLoading(false);
    }
  };
  
  const getUserAddressesSection = () => {
    if (addressLoading) {
      return (
        <div className={'mt-3'}>
          <Loading type={'mini'} />
        </div>
      );
    }
    
    if (!userAddresses.length) return null;

    return (
      <div className={styles['address-container']}>
        {userAddresses.map((record, i) => {
          const contactName = _.get(record, 'contactName', '');
          const contactPhone = _.get(record, 'contactPhone', '');
          return (
            <SingleAddressTile
              key={i}
              loading={addressDeleteID === record.objectId}
              contactName={contactName}
              contactPhone={contactPhone}
              addressDetails={record}
              setAddress={setAddress}
              deleteAddressFn={deleteAddressFn}
              toggleAddressModal={toggleAddressModal}
              setAddressToEdit={setAddressToEdit}
              showAddressActions={true}
              showInvalidAddresses={true}
            />
          );
        })}
      </div>
    );
  };

  const getUserDetailsSection = () => {
    const handleSelect = async (user: Record<string, any>) => {
      if (!showAddAddressButton) setShowAddAddressButton(true);
      setChannel(user);
      setUserAddresses([]);
      if (user.objectId) {
        setUserId(user.objectId);
        setAddressLoading(true);
        fetchUserAddressesFn(user.objectId);
        setSelectedChannelName(user.full_name);
      }
    };

    const renderAccountsSection = () => (
      <div>
        <AccountAutosuggest
          value={channel}
          onSelect={handleSelect}
          showUserSelect={true}
        />
      </div>
    );

    return (
      <div className={'mt-3'}>
        {userType === 'user' && (
          <div className={styles['autosuggest-input']}>
            <SearchBar
              isUser={true}
              value={channel.full_name || ''}
              // eslint-disable-next-line @typescript-eslint/camelcase
              setValue={value => setChannel({ ...channel, full_name: value })}
              searchAPI={searchUserByName}
              searchAPIParamKey={'term'}
              onSuggestionClicked={handleSelect}
            />
          </div>
        )}
        {userType === 'accounts' && renderAccountsSection()}
      </div>
    );
  };

  const getChannelOptions = () => {
    const handleChannelTypeChange = (value: string) => {
      setUserType(value);
      setChannel({});
      setUserAddresses([]);
      setShowAddAddressButton(false);
      setSelectedChannelName('');
    };

    return (
      <div className={'d-flex justify-content-between align-items-center'}>
        <div>
          <div className={styles['label']}>Sales Channel</div>
          <div className={'d-flex mt-1'}>
            <div
              className={'d-flex align-items-center mb-n3'}>
              <input type={'radio'} name={'customer-type'} value={'user'} onChange={e => handleChannelTypeChange(e.target.value)} />
              <div className={'label ml-1'}>Customer</div>
            </div>
            <div className={'d-flex align-items-center ml-5 mb-n3'}>
              <input type={'radio'} name={'customer-type'} value={'accounts'} onChange={e => handleChannelTypeChange(e.target.value)} />
              <div className={'label ml-1'}>Accounts</div>
            </div>
          </div>
        </div>
        {showAddAddressButton && (
          <ActionButton
            active={true}
            isLoading={false}
            onClick={toggleAddressModal}
            text={<>Address<span className={'ml-3'}>+</span></>}
          />
        )}
      </div>
    );
  };
  
  const handleAddAddress = () => {
    setAddressToEdit(undefined);
    toggleAddressModal();
  };
  
  return (
    <>
      {addressModalOpen && (
        <AddAddressModal
          modalOpen={addressModalOpen}
          address={addressToEdit ? stringifyAndParseObj(addressToEdit) : addressToEdit}
          user={channel}
          onSuccess={() => fetchUserAddressesFn(channel.objectId)}
          onModalClose={handleAddAddress}
        />
      )}
      <div className={styles['channel-select-container']}>
        {getChannelOptions()}
        {getUserDetailsSection()}
        {getUserAddressesSection()}
      </div>
    </>
  );
};

export default scriptLoader(
  [`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAP_KEY}&libraries=places`],
)(CustomerChannel);

